import React, { ReactElement, ComponentProps } from 'react';

import PropTypes from 'prop-types';

import theme, {
  stylePropType,
  StylableComponent,
} from '@adsk/alloy-react-theme';

import DefaultPageHeaderSubtitle from './layout/DefaultPageHeaderSubtitle';
import DefaultPageHeaderTitle from './layout/DefaultPageHeaderTitle';

type PageHeaderProps = StylableComponent<HTMLDivElement> & {
  title: string;
  subtitle?: string;
  isCollapsed?: boolean;
  renderTitle?: (
    props: ComponentProps<typeof DefaultPageHeaderTitle>
  ) => ReactElement;
  renderSubtitle?: (
    props: ComponentProps<typeof DefaultPageHeaderSubtitle>
  ) => ReactElement;
  renderTabs?: () => ReactElement;
};
/**
 * PageHeader composes a title, subtitle, tabs, and actions.
 */
const PageHeader = React.forwardRef<HTMLDivElement, PageHeaderProps>(
  (
    {
      className,
      children,
      style,
      title,
      subtitle,
      isCollapsed,
      renderTitle = (titleProps) => <DefaultPageHeaderTitle {...titleProps} />,
      renderSubtitle = (subtitleProps) => (
        <DefaultPageHeaderSubtitle {...subtitleProps} />
      ),
      renderTabs,
      ...props
    },
    ref
  ) => (
    <div
      {...props}
      ref={ref}
      className={className}
      css={[
        {
          width: '100%',
          color: theme.colors.charcoal900,
          ...(!renderTabs && {
            borderBottom: `1px solid ${theme.colors.charcoal200}`,
          }),
        },
        style,
      ]}
    >
      {renderTitle({ title, subtitle, isCollapsed })}
      {subtitle && renderSubtitle({ subtitle, isCollapsed })}
      {renderTabs && renderTabs()}
      {children}
    </div>
  )
);

PageHeader.displayName = 'PageHeader';

PageHeader.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Page title */
  title: PropTypes.string.isRequired,
  /** Render custom title */
  renderTitle: PropTypes.func,
  /** Page subtitle */
  subtitle: PropTypes.string,
  /** Render custom subtitle */
  renderSubtitle: PropTypes.func,
  /** Render tabs */
  renderTabs: PropTypes.func,
  /** header children */
  children: PropTypes.node,
};

export default Object.assign(PageHeader, {
  Title: DefaultPageHeaderTitle,
  Subtitle: DefaultPageHeaderSubtitle,
});
